import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { inject, NgModule } from '@angular/core';
import { MatNativeDateModule } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ActionPerformed, LocalNotifications } from '@capacitor/local-notifications';
import { FileOpener, FileOpenerOptions } from '@capacitor-community/file-opener';
import { provideHotToastConfig } from '@ngxpert/hot-toast';
import { MARKED_OPTIONS, MarkedRenderer, provideMarkdown } from 'ngx-markdown';

import { FirebaseDeviceService, ReachBlobDownloaderService } from '~app-client/core/services';
import {
	CLIENT_ENVIRONMENT,
	CommitHash,
	REACH_COMMIT_HASH,
	REACH_VERSION,
	ReachVersion,
} from '~app-client/core/tokens';
import { ClientEnvironment } from '~app-client/core/types';
import { OnboardingService } from '~app-client/onboarding/services';
import { OnboardingCondition } from '~app-client/onboarding/types';
import {
	ApiDownInterceptor,
	ApiNotReachableInterceptor,
	HandleHeadersInterceptor,
	MetadataInterceptor,
	QuotaInterceptor,
	ReplaceBackendUrlInterceptor,
	VersionErrorInterceptor,
} from '~app-client/root/interceptors';
import { AppPageComponent, AppPageModule } from '~app-client/root/page';
import { RootRouterModule } from '~app-client/root/router';
import { REACH_API_COMMON_HEADERS } from '~app-client-item/api/tokens/common-headers';
import { CHAT_DATA_RETRIEVAL_BASE_ROUTER_PATH } from '~app-client-item/chat-data-retrieval/tokens/base-router-path';
import { environment } from '~app-environment';
import { ReachPanModule } from '~app-ui/cdk/reach-pan';
import { REACH_API_COMMON_HEADERS as NEW_REACH_API_COMMON_HEADERS } from '~ng-common-lib/core/tokens/api-common-headers';
import { COMMON_CLIENT_CONFIG } from '~ng-common-lib/core/tokens/client-config';
import {
	COMMON_ENVIRONMENT,
	CommonEnvironment,
	CommonEnvironmentName,
} from '~ng-common-lib/core/tokens/environment';
import { provideSourcePreviewConfigurationFactory } from '~ng-common-lib/source-preview/tokens/configuration';
import { provideMenuConfiguration } from '~ng-common-lib/ui/menu/tokens/menu-config';

import { CLIENT_CONFIG_GENERATED } from '../../../environments/client-config.generated';
import { TranslocoRootModule } from './transloco.module';

declare const PACKAGE_VERSION: ReachVersion;
declare const COMMIT_HASH: CommitHash;

const appToCommonEnvironment = (env: ClientEnvironment): CommonEnvironment => {
	return {
		environmentName: environment.environmentName as unknown as CommonEnvironmentName,
		clientId: env.clientId,
		server: {
			url: environment.apiBaseUrl,
		},
		mainClient: {
			url: environment.frontendUrl,
		},
		handbook: {
			url: environment.handbookUrl,
		},
		myGenieServer: {
			url: environment.myGenieServer,
		},
		microsoft: {
			loginClientId: environment.socialSignIn.microsoftClientId,
			loginAuthority: environment.socialSignIn.microsoftAuthority,
			myGenieClientId: environment.socialSignIn.microsoftMyGenieClientId,
			myGenieAuthority: environment.socialSignIn.microsoftMyGenieAuthority,
			teamsIntegrationClientId: environment.socialSignIn.microsoftTeamsIntegrationClientId,
			teamsIntegrationAuthority: environment.socialSignIn.microsoftTeamsIntegrationAuthority,
			teamsAppId: environment.microsoftTeamsAppId ?? undefined,
			widgetSecurityClientId: environment.socialSignIn.microsoftWidgetSecurityClientId,
		},
	};
};

@NgModule({
	declarations: [],
	bootstrap: [AppPageComponent],
	imports: [
		AppPageModule,
		BrowserModule,
		BrowserAnimationsModule,
		ReachPanModule, // this is needed or hammer won't work.
		RootRouterModule,
		TranslocoRootModule,
		MatNativeDateModule, // without this imported in the root module, the material datepicker component doesn't work
	],
	providers: [
		provideHotToastConfig({
			dismissible: true,
			error: {
				className: 'hot-toast hot-toast-error',
			},
			success: {
				className: 'hot-toast hot-toast-success',
			},
			warning: {
				className: 'hot-toast hot-toast-warning',
			},
			info: {
				className: 'hot-toast hot-toast-info',
			},
		}),
		{ provide: REACH_VERSION, useValue: PACKAGE_VERSION },
		// required when tunneling through ngrok
		{ provide: REACH_API_COMMON_HEADERS, useValue: { 'ngrok-skip-browser-warning': 'true' } },
		{ provide: REACH_COMMIT_HASH, useValue: COMMIT_HASH },
		{ provide: CLIENT_ENVIRONMENT, useValue: environment },
		{
			provide: NEW_REACH_API_COMMON_HEADERS,
			useValue: { 'ngrok-skip-browser-warning': 'true' },
		},
		{
			provide: COMMON_ENVIRONMENT,
			useValue: appToCommonEnvironment(environment),
		},
		{
			provide: COMMON_CLIENT_CONFIG,
			useValue: CLIENT_CONFIG_GENERATED,
		},
		{
			provide: CHAT_DATA_RETRIEVAL_BASE_ROUTER_PATH,
			useValue: ['/', 'home', 'chat'],
		},
		{ provide: HTTP_INTERCEPTORS, useExisting: HandleHeadersInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useExisting: MetadataInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: ReplaceBackendUrlInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: VersionErrorInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: ApiDownInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: QuotaInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: ApiNotReachableInterceptor, multi: true },
		provideHttpClient(withInterceptorsFromDi()),
		provideMarkdown({
			markedOptions: {
				provide: MARKED_OPTIONS,
				useFactory: () => {
					const renderer = new MarkedRenderer();
					renderer.link = (config) => {
						return `<a href="${config.href}" target="_blank">${config.text}</a>`;
					};
					return {
						renderer,
					};
				},
			},
		}),
		provideMenuConfiguration({
			onMenuOpened: () => {
				// send custom event 'menu_opened'
				window.dispatchEvent(new CustomEvent('menu_opened', {}));
			},
			onMenuClosed: () => {
				// send custom event 'menu_closed'
				window.dispatchEvent(new CustomEvent('menu_closed', {}));
			},
		}),
		provideSourcePreviewConfigurationFactory(() => {
			const blobDownloader = inject(ReachBlobDownloaderService);

			return {
				downloadLogic: async (url: string, name: string) => {
					await blobDownloader.downloadFile({
						name: name || `source`,
						fileUrl: url,
					});
				},
			};
		}),
	],
})
export class AppModule {
	private readonly onboardingService = inject(OnboardingService);

	constructor(private readonly firebaseDeviceService: FirebaseDeviceService) {
		this.firebaseDeviceService.init();

		LocalNotifications.addListener(
			'localNotificationActionPerformed',
			async (notificationAction: ActionPerformed) => {
				try {
					const extraData = notificationAction.notification.extra;

					if ('filePath' in extraData && 'contentType' in extraData) {
						const filePath = extraData.filePath;
						const contentType = extraData.contentType;
						const fileOpenerOptions: FileOpenerOptions = {
							filePath,
							contentType,
							openWithDefault: true,
						};
						await FileOpener.open(fileOpenerOptions);
					}
				} catch (error) {}
			}
		);

		// listen for custom event: 'menu_opened'
		window.addEventListener('menu_opened', () => {
			this.onboardingService.addCondition(OnboardingCondition.GENERIC_MENU_FULLY_OPENED);
		});

		// listen for custom event: 'menu_closed'
		window.addEventListener('menu_closed', () => {
			this.onboardingService.removeCondition(OnboardingCondition.GENERIC_MENU_FULLY_OPENED);
		});
	}
}
